<script>
import StoreSearchForm from '@phoenix/mixins/components/store/StoreSearchForm';
import { PhxAutocomplete } from '@phx-styleguides/als';
import { mapActions, mapGetters, mapState } from 'vuex';
import { PhxsgButton } from '@sg/src/View/Components/Atoms/Button';

export default {
  components: {
    PhxAutocomplete,
    PhxsgButton,
  },

  mixins: [StoreSearchForm],

  props: {
    boutiqueOnly: {
      type: Boolean,
      default: false,
    },
    withHeading: {
      type: Boolean,
      default: true,
    },
    fullWidth: {
      type: Boolean,
      default: false,
    },
    formStyle: {
      type: Object,
      default: () => ({}),
    },
  },

  data() {
    return {
      formData: {
        query: '',
      },
      isPositionLoading: false,
    };
  },

  computed: {
    ...mapState('storeSearch', ['placeSuggestions', 'isSuggestionLoading', 'isLoading']),

    ...mapGetters('storeSearch', ['searchPerformed']),

    options() {
      return this.placeSuggestions.map((place) => ({
        key: place.key,
        value: place.suggestion,
        highlightedText: place.formattedSuggestion,
      }));
    },

    noResultsText() {
      return this.$t(
        this.formData.query === '' ? 'StoreSearch.suggestions.provideSearchTerm' : 'StoreSearch.suggestions.noResults',
      );
    },

    fieldClasses() {
      return [
        'stores-search-form__field',
        {
          'stores-search-form__field--full': this.fullWidth,
        },
      ];
    },
  },

  watch: {
    isLoading: {
      handler(value) {
        if (this.isPositionLoading) {
          this.setPositionBtnLoader(value);
        } else {
          this.setButtonLoading(value);
        }
      },
    },
  },

  created() {
    if (this.inputValue) {
      this.formData.query = this.inputValue;
    }
  },

  mounted() {
    this.setBoutiqueOnly(this.boutiqueOnly);
  },

  methods: {
    ...mapActions('storeSearch', ['setBoutiqueOnly']),

    onQuery(query) {
      this.formData.query = query;
      this.onKeywordInput({ query });
    },

    setButtonLoading(isLoading = true) {
      if (isLoading) {
        Toolkit.helpers.buttons.addLoadingState(this.$refs.submitButton);
      } else {
        Toolkit.helpers.buttons.removeLoadingState(this.$refs.submitButton);
      }
    },

    setPositionBtnLoader(loading) {
      this.isPositionLoading = loading;
      if (loading) {
        Toolkit.helpers.buttons.addLoadingState(this.$refs.positionBtn, { text: this.$t('StoreSearch.geolocating') });
      } else {
        Toolkit.helpers.buttons.removeLoadingState(this.$refs.positionBtn);
      }
    },
  },
};
</script>

<template>
  <div data-cy="store-search-form">
    <div v-if="withHeading" class="headings headings--base">
      <!-- TODO: migration: replace by the heading component -->
      <h1>
        {{ $t('Store.storeSelector.searchABoutique') }}
      </h1>
    </div>
    <form :style="formStyle" @submit.prevent="onSubmit(formData)">
      <div class="stores-search-form">
        <div v-if="isSuggestionActivated" :class="fieldClasses">
          <phx-autocomplete
            id="store-locator-autocomplete"
            name="store-locator-autocomplete"
            label=""
            open-button-icon="chevron-down"
            clear-button-icon="clear"
            :clear-button-label="$t('StoreSearch.suggestions.clear')"
            :open-button-label="$t('StoreSearch.suggestions.open')"
            :no-result-text="noResultsText"
            :loading-text="$t('StoreSearch.suggestions.loading')"
            :loading="isSuggestionLoading"
            :options="options"
            error=""
            :placeholder="placeholder"
            @autocomplete:query="onQuery"
            @autocomplete:change="onSubmit"
          />
        </div>

        <div v-else class="stores-search-form__field">
          <div class="form-field">
            <input v-model="formData.query" class="input" :placeholder="placeholder" autocorrect="off" />
          </div>
        </div>

        <phxsg-button ref="submitButton" type="submit" class="stores-search-form__submit" @click="onSubmit(formData)">
          {{ $t('Search.submit') }}
        </phxsg-button>

        <phxsg-button
          ref="positionBtn"
          icon-only
          variant="secondary"
          icon-id="geoloc"
          class="stores-search-form__geo-loc"
          @click.prevent="useMyPosition"
        >
          {{ $t('StoreSearch.useMyPosition') }}
        </phxsg-button>
      </div>
    </form>
  </div>
</template>
